import * as React from 'react';
import { Trash2, ShieldCheck, Plus, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import {
  useAdminAddApplePayDomain,
  useAdminProbeApplePayDomain,
  useAdminRemoveApplePayDomain,
  useAdminDomainAssociationFile,
  useAdminUpdateDomainAssociationFile,
} from '@/lib/api/apple-pay-registrations';
import { DomainAssociationFileDialog } from '@/components/apple-pay/DomainAssociationFileDialog';
import { formatDate, type ApplePayRegistration } from './utils';

function AddDomainDialog({
  registration,
  open,
  onOpenChange,
}: {
  registration: ApplePayRegistration;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const add = useAdminAddApplePayDomain();
  const [domain, setDomain] = React.useState('');
  const [accountId, setAccountId] = React.useState('');
  const [storeId, setStoreId] = React.useState('');
  const [associationFile, setAssociationFile] = React.useState('');

  const reset = () => {
    setDomain('');
    setAccountId('');
    setStoreId('');
    setAssociationFile('');
  };

  const handleAdd = () => {
    const trimmed = domain.trim();
    if (!trimmed) return;
    add.mutate(
      {
        registrationId: registration.id,
        domain: trimmed,
        accountId: accountId.trim() || undefined,
        storeId: storeId.trim() || undefined,
        associationFileContent: associationFile.trim() || undefined,
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add a domain</DialogTitle>
          <DialogDescription>
            {registration.isPlatform
              ? 'On this shared platform certificate, the domain is attributed to the merchant account/store that owns it. Leave Account/Store blank — they are auto-resolved from the storefront when the domain is already set up.'
              : 'The domain is attached to this registration and served its association file immediately.'}{' '}
            After adding: verify the domain in the Apple Developer portal, then use Probe to activate it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Domain</Label>
            <Input
              placeholder="checkout.example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className="font-mono text-sm"
              autoFocus
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Account (optional — auto-resolved)</Label>
              <Input
                placeholder="acc_…"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Store (optional)</Label>
              <Input
                placeholder="store_…"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Association file (optional — can add later)</Label>
            <Textarea
              placeholder="Paste this domain's apple-developer-merchantid-domain-association file…"
              value={associationFile}
              onChange={(e) => setAssociationFile(e.target.value)}
              className="min-h-[100px] font-mono text-xs"
            />
            <p className="text-muted-foreground text-xs">
              Apple issues a distinct file per domain. Leave blank to fall back to the registration&rsquo;s
              file.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!domain.trim() || add.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            {add.isPending ? 'Adding…' : 'Add domain'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DomainsSection({ registration }: { registration: ApplePayRegistration }) {
  const probe = useAdminProbeApplePayDomain();
  const remove = useAdminRemoveApplePayDomain();
  const [addOpen, setAddOpen] = React.useState(false);
  const [fileDomain, setFileDomain] = React.useState<{ id: string; domain: string } | null>(null);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Domains ({registration.domains.length})</h4>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add domain
        </Button>
      </div>

      {registration.domains.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No domains attached yet. Add the checkout domain(s) this certificate should serve.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Account / Store</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Last probe</TableHead>
                <TableHead className="w-[220px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registration.domains.map((domain) => (
                <TableRow key={domain.id}>
                  <TableCell className="whitespace-nowrap font-mono text-sm">{domain.domain}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {domain.accountId ?? '—'}
                    {domain.storeId ? ` / ${domain.storeId}` : ''}
                  </TableCell>
                  <TableCell>
                    {domain.active ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {domain.hasAssociationFile ? (
                      <Badge variant="outline" className="text-green-700">
                        Set
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Missing
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap text-xs">
                    {formatDate(domain.verifiedAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap text-xs">
                    {formatDate(domain.lastProbeAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFileDomain({ id: domain.id, domain: domain.domain })}
                        title="View / replace association file"
                      >
                        <FileText className="mr-1 h-3.5 w-3.5" />
                        File
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => probe.mutate({ domainId: domain.id })}
                        disabled={probe.isPending}
                      >
                        <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                        Probe
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive h-8 w-8"
                        onClick={() => remove.mutate({ domainId: domain.id })}
                        disabled={remove.isPending}
                        title="Remove domain"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddDomainDialog registration={registration} open={addOpen} onOpenChange={setAddOpen} />

      {fileDomain && (
        <DomainAssociationFileDialog
          domainId={fileDomain.id}
          domain={fileDomain.domain}
          open={!!fileDomain}
          onOpenChange={(o) => !o && setFileDomain(null)}
          useFileQuery={useAdminDomainAssociationFile}
          useUpdateFile={useAdminUpdateDomainAssociationFile}
        />
      )}
    </section>
  );
}
